import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { OverviewScreen } from '@/features/trading/overview-screen';
import {
  overviewQuery,
  pnlQuery,
  positionsQuery,
  signalsQuery,
  tradesQuery,
} from '@/features/trading/trading-api';
import { meQuery } from '@/lib/queries';

export const Route = createFileRoute('/_app/')({
  // `signals` = the signals sheet is open, on that decision filter.
  validateSearch: z.object({
    signals: z.enum(['all', 'copied', 'skipped', 'failed']).optional().catch(undefined),
  }),
  // Warm the caches without blocking the screen: every panel has its own loading state.
  loader: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery);
    const mode = me.settings.executionMode;
    void queryClient.prefetchQuery(overviewQuery(mode));
    void queryClient.prefetchQuery(pnlQuery(mode));
    void queryClient.prefetchInfiniteQuery(positionsQuery('open', mode));
    void queryClient.prefetchInfiniteQuery(signalsQuery('all'));
    void queryClient.prefetchInfiniteQuery(tradesQuery);
  },
  component: OverviewRoute,
});

function OverviewRoute() {
  const { signals } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  // Opening the sheet from this screen pushes one history entry, so Back closes it. Closing it
  // then steps back over that entry instead of stacking another. Opened straight from a URL
  // (reload, a redirect, a shared link), closing just drops the param.
  const pushed = useRef(false);
  useEffect(() => {
    if (signals === undefined) pushed.current = false;
  }, [signals]);

  return (
    <OverviewScreen
      signals={signals}
      onOpenSignals={() => {
        pushed.current = true;
        void navigate({ search: (prev) => ({ ...prev, signals: 'all' }) });
      }}
      onSignalsFilter={(next) =>
        void navigate({ search: (prev) => ({ ...prev, signals: next }), replace: true })
      }
      onCloseSignals={() => {
        if (pushed.current) {
          pushed.current = false;
          router.history.back();
        } else {
          void navigate({ search: (prev) => ({ ...prev, signals: undefined }), replace: true });
        }
      }}
    />
  );
}
